import React, {memo, type ReactNode, useEffect, useMemo} from 'react';
import Helmet from 'react-helmet';

import config from '../../config';
import {
  DEFAULT_LANGUAGE,
  DISCUSSION_CATEGORY_ID,
  getHREFLang,
  type ReportMetadata,
  type TranslationData,
  useReportMetadata,
} from '../../util/gallery';
import {reportView} from '../../util/urls';

type ReportPageLanguageTagsProps = {
  translationData: TranslationData;
};

const ReportPageLanguageTagsComp = ({
  translationData,
}: ReportPageLanguageTagsProps) => {
  const {
    inputReport: currentReport,
    originalReport,
    translations,
  } = translationData;

  useEffect(() => {
    const htmlEl = document.querySelector('html');
    if (htmlEl == null) {
      return;
    }

    htmlEl.lang = getHREFLang(currentReport.language);
    return () => {
      htmlEl.lang = getHREFLang(DEFAULT_LANGUAGE);
    };
  }, [currentReport]);

  const reportIDs = useMemo(() => {
    return [originalReport, ...translations].map(({id}) => id);
  }, [originalReport, translations]);
  const {reportMetadatas} = useReportMetadata(reportIDs);

  if (reportMetadatas == null) {
    return null;
  }

  const reportMetadataByID: Map<string, ReportMetadata> = new Map(
    reportMetadatas.map(r => [r.id, r])
  );

  const metaTags: ReactNode[] = [];
  let defaultMetaTag: ReactNode | null = null;
  for (const {id, language} of [originalReport, ...translations]) {
    const metadata = reportMetadataByID.get(id);
    if (metadata == null) {
      continue;
    }

    const {project, entityName, displayName, tags} = metadata;
    const href =
      config.HOST +
      reportView({
        entityName: project?.entityName ?? entityName,
        projectName: project?.name ?? '',
        reportID: id,
        reportName: displayName,
        isDiscussion: tags?.some(t => t.id === DISCUSSION_CATEGORY_ID) ?? false,
      });
    const hrefLang = getHREFLang(language);

    metaTags.push(
      <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
    );

    if (id === originalReport.id) {
      defaultMetaTag = (
        <link
          key="x-default"
          rel="alternate"
          hrefLang="x-default"
          href={href}
        />
      );
    }
  }
  if (defaultMetaTag != null) {
    metaTags.push(defaultMetaTag);
  }

  return <Helmet>{metaTags}</Helmet>;
};

export const ReportPageLanguageTags = memo(ReportPageLanguageTagsComp);
